
<template>
  <section class="calculator-module">
    <div class="module-header">
      <h2>{{ t('feeding.title').value }}</h2>
      <span class="toggle-icon">▼</span>
    </div>
    <div class="module-content">
      <div class="calculator-intro">
        <h3>{{ t('feeding.subtitle').value }}</h3>
        <p>{{ t('feeding.description').value }}</p>
      </div>
      
      <div class="calculator-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'coral' }"
          @click="activeTab = 'coral'"
        >
          {{ t('feeding.tabs.coral').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'fish' }"
          @click="activeTab = 'fish'"
        >
          {{ t('feeding.tabs.fish').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'schedule' }"
          @click="activeTab = 'schedule'"
        >
          {{ t('feeding.tabs.schedule').value }}
        </button>
      </div>
      
      <!-- 珊瑚粮计算 -->
      <div v-if="activeTab === 'coral'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('feeding.sections.basicInfo').value }}</h4>
          <div class="form-group">
            <label for="coral_volume">{{ t('feeding.labels.waterVolume').value }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="coral_volume"
                v-model="coralData.volume"
                class="form-control"
                :placeholder="t('feeding.placeholders.waterVolume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolumeCoral"
                type="button"
              >
                {{ t('feeding.buttons.useStoredVolume').value }}
              </button>
            </div>
            <small class="form-hint" v-if="storedWaterVolume">
              {{ t('feeding.hints.storedVolume').value }}: {{ storedWaterVolume }} L
            </small>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('feeding.sections.coralInfo').value }}</h4>
          <div class="form-group">
            <label for="coral_type">{{ t('feeding.labels.coralType').value }}</label>
            <select
              id="coral_type"
              v-model="coralData.coral_type"
              class="select-control"
            >
              <option value="mixed">{{ t('feeding.options.coralType.mixed').value }}</option>
              <option value="sps">{{ t('feeding.options.coralType.sps').value }}</option>
              <option value="lps">{{ t('feeding.options.coralType.lps').value }}</option>
              <option value="soft">{{ t('feeding.options.coralType.soft').value }}</option>
              <option value="nps">{{ t('feeding.options.coralType.nps').value }}</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="coral_density">{{ t('feeding.labels.coralDensity').value }}</label>
            <select
              id="coral_density"
              v-model="coralData.coral_density"
              class="select-control"
            >
              <option value="low">{{ t('feeding.options.coralDensity.low').value }}</option>
              <option value="medium">{{ t('feeding.options.coralDensity.medium').value }}</option>
              <option value="high">{{ t('feeding.options.coralDensity.high').value }}</option>
              <option value="ultra">{{ t('feeding.options.coralDensity.ultra').value }}</option>
            </select>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('feeding.sections.coralFoodSelection').value }}</h4>
          <div class="form-group">
            <label for="coral_food">{{ t('feeding.labels.coralFoodType').value }}</label>
            <select
              id="coral_food"
              v-model="coralData.coral_food"
              class="select-control"
            >
              <option value="reef-roids">{{ t('feeding.options.coralFood.reef-roids').value }}</option>
              <option value="red-sea-reef-energy">{{ t('feeding.options.coralFood.red-sea-reef-energy').value }}</option>
              <option value="fauna-marin-coral-dust">{{ t('feeding.options.coralFood.fauna-marin-coral-dust').value }}</option>
              <option value="brightwell-coral-aminos">{{ t('feeding.options.coralFood.brightwell-coral-aminos').value }}</option>
              <option value="two-little-fishies-phyto">{{ t('feeding.options.coralFood.two-little-fishies-phyto').value }}</option>
              <option value="custom">{{ t('feeding.options.coralFood.custom').value }}</option>
            </select>
          </div>
          
          <div v-if="coralData.coral_food === 'custom'" class="form-group">
            <label for="custom_coral_dosage">{{ t('feeding.labels.customDosage').value }}</label>
            <input
              type="number"
              id="custom_coral_dosage"
              v-model="coralData.custom_coral_dosage"
              class="form-control"
              :placeholder="t('feeding.placeholders.customDosage').value"
              step="0.1"
              min="0.1"
              max="50"
            >
          </div>
          
          <div class="form-group">
            <label for="feeding_frequency">{{ t('feeding.labels.feedingFrequency').value }}</label>
            <select
              id="feeding_frequency"
              v-model="coralData.feeding_frequency"
              class="select-control"
            >
              <option value="daily">{{ t('feeding.options.feedingFrequency.daily').value }}</option>
              <option value="twice">{{ t('feeding.options.feedingFrequency.twice').value }}</option>
              <option value="every-other-day">{{ t('feeding.options.feedingFrequency.every-other-day').value }}</option>
              <option value="weekly">{{ t('feeding.options.feedingFrequency.weekly').value }}</option>
            </select>
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculateCoralFeeding"
          :disabled="!canCalculateCoral"
        >
          {{ t('feeding.buttons.calculateCoral').value }}
        </button>
      </div>
      
      <!-- 鱼粮计算 -->
      <div v-if="activeTab === 'fish'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('feeding.sections.basicInfo').value }}</h4>
          <div class="form-group">
            <label for="fish_volume">{{ t('feeding.labels.waterVolume').value }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="fish_volume"
                v-model="fishData.volume"
                class="form-control"
                :placeholder="t('feeding.placeholders.waterVolume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolumeFish"
                type="button"
              >
                {{ t('feeding.buttons.useStoredVolume').value }}
              </button>
            </div>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('feeding.sections.fishInfo').value }}</h4>
          <div class="form-group">
            <label for="fish_count">{{ t('feeding.labels.fishCount').value }}</label>
            <input
              type="number"
              id="fish_count"
              v-model="fishData.fish_count"
              class="form-control"
              :placeholder="t('feeding.placeholders.fishCount').value"
              step="1"
              min="1"
              max="50"
            >
          </div>
          
          <div class="form-group">
            <label for="fish_size">{{ t('feeding.labels.fishSize').value }}</label>
            <select
              id="fish_size"
              v-model="fishData.fish_size"
              class="select-control"
            >
              <option value="small">{{ t('feeding.options.fishSize.small').value }}</option>
              <option value="medium">{{ t('feeding.options.fishSize.medium').value }}</option>
              <option value="large">{{ t('feeding.options.fishSize.large').value }}</option>
              <option value="xlarge">{{ t('feeding.options.fishSize.xlarge').value }}</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="fish_activity">{{ t('feeding.labels.fishActivity').value }}</label>
            <select
              id="fish_activity"
              v-model="fishData.fish_activity"
              class="select-control"
            >
              <option value="low">{{ t('feeding.options.fishActivity.low').value }}</option>
              <option value="medium">{{ t('feeding.options.fishActivity.medium').value }}</option>
              <option value="high">{{ t('feeding.options.fishActivity.high').value }}</option>
            </select>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('feeding.sections.fishFoodSelection').value }}</h4>
          <div class="form-group">
            <label for="fish_food">{{ t('feeding.labels.fishFoodType').value }}</label>
            <select
              id="fish_food"
              v-model="fishData.fish_food"
              class="select-control"
            >
              <option value="flakes">{{ t('feeding.options.fishFood.flakes').value }}</option>
              <option value="pellets">{{ t('feeding.options.fishFood.pellets').value }}</option>
              <option value="frozen">{{ t('feeding.options.fishFood.frozen').value }}</option>
              <option value="nori">{{ t('feeding.options.fishFood.nori').value }}</option>
              <option value="custom">{{ t('feeding.options.fishFood.custom').value }}</option>
            </select>
          </div>
          
          <div v-if="fishData.fish_food === 'custom'" class="form-group">
            <label for="custom_fish_dosage">{{ t('feeding.labels.customDosage').value }}</label>
            <input
              type="number"
              id="custom_fish_dosage"
              v-model="fishData.custom_fish_dosage"
              class="form-control"
              :placeholder="t('feeding.placeholders.customDosage').value"
              step="0.1"
              min="0.1"
              max="20"
            >
          </div>
          
          <div class="form-group">
            <label for="fish_feeding_frequency">{{ t('feeding.labels.feedingFrequency').value }}</label>
            <select
              id="fish_feeding_frequency"
              v-model="fishData.feeding_frequency"
              class="select-control"
            >
              <option value="twice">{{ t('feeding.options.feedingFrequency.twice').value }}</option>
              <option value="three">{{ t('feeding.options.feedingFrequency.three').value }}</option>
              <option value="multiple">{{ t('feeding.options.feedingFrequency.multiple').value }}</option>
              <option value="once">{{ t('feeding.options.feedingFrequency.once').value }}</option>
            </select>
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculateFishFeeding"
          :disabled="!canCalculateFish"
        >
          {{ t('feeding.buttons.calculateFish').value }}
        </button>
      </div>
      
      <!-- 喂食计划 -->
      <div v-if="activeTab === 'schedule'" class="schedule-section">
        <div class="schedule-card">
          <h4>{{ t('feeding.schedule.title').value }}</h4>
          
          <div class="schedule-category">
            <h5>{{ t('feeding.schedule.coralFeedingPlan').value }}</h5>
            <div class="schedule-list">
              <div class="schedule-item">
                <h6>{{ t('feeding.schedule.coralTypes.sps').value }}</h6>
                <ul>
                  <li>{{ t('feeding.schedule.coralTips.sps.aminoAcids').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.sps.coralFood').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.sps.phytoplankton').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.sps.eveningFeeding').value }}</li>
                </ul>
              </div>
              <div class="schedule-item">
                <h6>{{ t('feeding.schedule.coralTypes.lps').value }}</h6>
                <ul>
                  <li>{{ t('feeding.schedule.coralTips.lps.coralFood').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.lps.targetFeeding').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.lps.aminoAcids').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.lps.observeReaction').value }}</li>
                </ul>
              </div>
              <div class="schedule-item">
                <h6>{{ t('feeding.schedule.coralTypes.nps').value }}</h6>
                <ul>
                  <li>{{ t('feeding.schedule.coralTips.nps.dailyFeeding').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.nps.variousSizes').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.nps.phytoplanktonRotifer').value }}</li>
                  <li>{{ t('feeding.schedule.coralTips.nps.strongFlow').value }}</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="schedule-category">
            <h5>{{ t('feeding.schedule.fishFeedingPlan').value }}</h5>
            <div class="schedule-list">
              <div class="schedule-item">
                <h6>{{ t('feeding.schedule.fishTypes.omnivore').value }}</h6>
                <ul>
                  <li>{{ t('feeding.schedule.fishTips.omnivore.flakesPellets').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.omnivore.frozen').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.omnivore.nori').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.omnivore.twoMinuteRule').value }}</li>
                </ul>
              </div>
              <div class="schedule-item">
                <h6>{{ t('feeding.schedule.fishTypes.carnivore').value }}</h6>
                <ul>
                  <li>{{ t('feeding.schedule.fishTips.carnivore.frozenShrimpFish').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.carnivore.pelletsSupplement').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.carnivore.vitaminSupplement').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.carnivore.avoidObesity').value }}</li>
                </ul>
              </div>
              <div class="schedule-item">
                <h6>{{ t('feeding.schedule.fishTypes.herbivore').value }}</h6>
                <ul>
                  <li>{{ t('feeding.schedule.fishTips.herbivore.nori').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.herbivore.algaeFlakes').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.herbivore.freshVegetables').value }}</li>
                  <li>{{ t('feeding.schedule.fishTips.herbivore.enoughAlgae').value }}</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="schedule-category">
            <h5>{{ t('feeding.schedule.feedingNotes').value }}</h5>
            <div class="tips-list">
              <div class="tip-item">
                <span class="tip-icon">💡</span>
                <span class="tip-text">{{ t('feeding.schedule.tips.observeFishBehavior').value }}</span>
              </div>
              <div class="tip-item">
                <span class="tip-icon">⚠️</span>
                <span class="tip-text">{{ t('feeding.schedule.tips.avoidOverfeeding').value }}</span>
              </div>
              <div class="tip-item">
                <span class="tip-icon">🕒</span>
                <span class="tip-text">{{ t('feeding.schedule.tips.fixedFeedingTime').value }}</span>
              </div>
              <div class="tip-item">
                <span class="tip-icon">🧹</span>
                <span class="tip-text">{{ t('feeding.schedule.tips.checkLeftovers').value }}</span>
              </div>
              <div class="tip-item">
                <span class="tip-icon">🌊</span>
                <span class="tip-text">{{ t('feeding.schedule.tips.turnOffPump').value }}</span>
              </div>
              <div class="tip-item">
                <span class="tip-icon">📊</span>
                <span class="tip-text">{{ t('feeding.schedule.tips.monitorNutrients').value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 珊瑚粮计算结果 -->
      <div v-if="coralResults.show" class="results-section">
        <div class="results-card">
          <h4>{{ t('feeding.results.coral.title').value }}</h4>
          
          <div class="result-grid">
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.coral.singleDosage').value }}:</span>
              <span class="result-value">{{ coralResults.singleDosage }} mL</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.coral.dailyDosage').value }}:</span>
              <span class="result-value">{{ coralResults.dailyDosage }} mL</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.coral.foodType').value }}:</span>
              <span class="result-value">{{ coralResults.foodType }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.coral.frequency').value }}:</span>
              <span class="result-value">{{ coralResults.frequency }}</span>
            </div>
          </div>
          
          <div class="feeding-advice">
            <h5>{{ t('feeding.results.coral.advice').value }}</h5>
            <p>{{ coralResults.advice }}</p>
            <ul>
              <li v-for="tip in coralResults.feedingTips" :key="tip">{{ tip }}</li>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- 鱼粮计算结果 -->
      <div v-if="fishResults.show" class="results-section">
        <div class="results-card">
          <h4>{{ t('feeding.results.fish.title').value }}</h4>
          
          <div class="result-grid">
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.fish.singleDosage').value }}:</span>
              <span class="result-value">{{ fishResults.singleDosage }} g</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.fish.dailyTotal').value }}:</span>
              <span class="result-value">{{ fishResults.dailyTotal }} g</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.fish.foodType').value }}:</span>
              <span class="result-value">{{ fishResults.foodType }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('feeding.results.fish.frequency').value }}:</span>
              <span class="result-value">{{ fishResults.frequency }}</span>
            </div>
          </div>
          
          <div class="feeding-advice">
            <h5>{{ t('feeding.results.fish.advice').value }}</h5>
            <p>{{ fishResults.advice }}</p>
            <ul>
              <li v-for="tip in fishResults.feedingTips" :key="tip">{{ tip }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useCalculatorStore } from '../stores/calculator'
import { useI18n } from '../composables/useI18n'

const calculatorStore = useCalculatorStore()
const { t } = useI18n()

// 标签页状态
const activeTab = ref<'coral' | 'fish' | 'schedule'>('coral')

// 珊瑚粮数据
const coralData = reactive({
  volume: '',
  coral_type: 'mixed',
  coral_density: 'medium',
  coral_food: 'reef-roids',
  custom_coral_dosage: '5',
  feeding_frequency: 'weekly'
})

// 鱼粮数据
const fishData = reactive({
  volume: '',
  fish_count: '',
  fish_size: 'medium',
  fish_activity: 'medium',
  fish_food: 'flakes',
  custom_fish_dosage: '2',
  feeding_frequency: 'twice'
})

// 计算结果
const coralResults = reactive({
  show: false,
  singleDosage: 0,
  dailyDosage: 0,
  foodType: '',
  frequency: '',
  advice: '',
  feedingTips: [] as string[]
})

const fishResults = reactive({
  show: false,
  singleDosage: 0,
  dailyTotal: 0,
  foodType: '',
  frequency: '',
  advice: '',
  feedingTips: [] as string[]
})

// 计算属性
const storedWaterVolume = computed(() => {
  return calculatorStore.waterVolume
})

const canCalculateCoral = computed(() => {
  return coralData.volume
})

const canCalculateFish = computed(() => {
  return fishData.volume && fishData.fish_count
})

// 使用已保存的水体体积
const useStoredVolumeCoral = () => {
  if (storedWaterVolume.value) {
    coralData.volume = storedWaterVolume.value.toString()
  }
}

const useStoredVolumeFish = () => {
  if (storedWaterVolume.value) {
    fishData.volume = storedWaterVolume.value.toString()
  }
}

// 计算珊瑚粮剂量
const calculateCoralFeeding = () => {
  const volume = parseFloat(coralData.volume)

  let dosagePer100L = 0
  let foodTypeName = ''
  let advice = ''
  let feedingTips: string[] = []

  // 根据珊瑚粮类型确定基础剂量和翻译键
  const foodKey = coralData.coral_food
  switch (foodKey) {
    case 'reef-roids':
      dosagePer100L = 2
      foodTypeName = t('feeding.foodDetails.coral.reef-roids.foodTypeName').value
      advice = t('feeding.foodDetails.coral.reef-roids.advice').value
      {
        const tips = t('feeding.foodDetails.coral.reef-roids.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'red-sea-reef-energy':
      dosagePer100L = 5
      foodTypeName = t('feeding.foodDetails.coral.red-sea-reef-energy.foodTypeName').value
      advice = t('feeding.foodDetails.coral.red-sea-reef-energy.advice').value
      {
        const tips = t('feeding.foodDetails.coral.red-sea-reef-energy.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'fauna-marin-coral-dust':
      dosagePer100L = 1
      foodTypeName = t('feeding.foodDetails.coral.fauna-marin-coral-dust.foodTypeName').value
      advice = t('feeding.foodDetails.coral.fauna-marin-coral-dust.advice').value
      {
        const tips = t('feeding.foodDetails.coral.fauna-marin-coral-dust.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'brightwell-coral-aminos':
      dosagePer100L = 1
      foodTypeName = t('feeding.foodDetails.coral.brightwell-coral-aminos.foodTypeName').value
      advice = t('feeding.foodDetails.coral.brightwell-coral-aminos.advice').value
      {
        const tips = t('feeding.foodDetails.coral.brightwell-coral-aminos.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'two-little-fishies-phyto':
      dosagePer100L = 10
      foodTypeName = t('feeding.foodDetails.coral.two-little-fishies-phyto.foodTypeName').value
      advice = t('feeding.foodDetails.coral.two-little-fishies-phyto.advice').value
      {
        const tips = t('feeding.foodDetails.coral.two-little-fishies-phyto.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'custom':
      dosagePer100L = parseFloat(coralData.custom_coral_dosage)
      foodTypeName = t('feeding.foodDetails.coral.custom.foodTypeName').value
      advice = t('feeding.foodDetails.coral.custom.advice').value
      {
        const tips = t('feeding.foodDetails.coral.custom.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
  }

  // 根据珊瑚类型和密度调整剂量
  dosagePer100L = adjustCoralDosage(dosagePer100L, coralData.coral_type, coralData.coral_density)

  // 计算剂量
  const singleDosage = (dosagePer100L * volume) / 100
  
  // 计算每日剂量
  let dailyDosage = singleDosage
  let frequency = ''
  switch (coralData.feeding_frequency) {
    case 'daily':
      frequency = t('feeding.options.feedingFrequency.daily').value
      break
    case 'twice':
      dailyDosage = singleDosage * 2
      frequency = t('feeding.options.feedingFrequency.twice').value
      break
    case 'every-other-day':
      dailyDosage = singleDosage * 0.5
      frequency = t('feeding.options.feedingFrequency.every-other-day').value
      break
    case 'weekly':
      dailyDosage = singleDosage * 0.43 // 每周3次平均
      frequency = t('feeding.options.feedingFrequency.weekly').value
      break
  }

  coralResults.singleDosage = Math.round(singleDosage * 100) / 100
  coralResults.dailyDosage = Math.round(dailyDosage * 100) / 100
  coralResults.foodType = foodTypeName
  coralResults.frequency = frequency
  coralResults.advice = advice
  coralResults.feedingTips = feedingTips
  coralResults.show = true
}

// 调整珊瑚粮剂量
const adjustCoralDosage = (baseDosage: number, coralType: string, density: string): number => {
  let adjusted = baseDosage

  // 根据珊瑚类型调整
  switch (coralType) {
    case 'sps':
      adjusted *= 0.8 // SPS需要较少食物
      break
    case 'lps':
      adjusted *= 1.2 // LPS需要较多食物
      break
    case 'nps':
      adjusted *= 2.0 // NPS需要大量食物
      break
    case 'soft':
      adjusted *= 0.6 // 软珊瑚需要很少食物
      break
  }

  // 根据密度调整
  switch (density) {
    case 'low':
      adjusted *= 0.5
      break
    case 'high':
      adjusted *= 1.5
      break
    case 'ultra':
      adjusted *= 2.0
      break
  }

  return adjusted
}

// 计算鱼粮剂量
const calculateFishFeeding = () => {
  // 解析体积用于计算
  parseFloat(fishData.volume)
  const fishCount = parseFloat(fishData.fish_count)

  let baseDosagePerFish = 0
  let foodTypeName = ''
  let advice = ''
  let feedingTips: string[] = []

  // 根据鱼粮类型确定基础剂量和翻译键
  const foodKey = fishData.fish_food
  switch (foodKey) {
    case 'flakes':
      baseDosagePerFish = 0.1
      foodTypeName = t('feeding.foodDetails.fish.flakes.foodTypeName').value
      advice = t('feeding.foodDetails.fish.flakes.advice').value
      {
        const tips = t('feeding.foodDetails.fish.flakes.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'pellets':
      baseDosagePerFish = 0.2
      foodTypeName = t('feeding.foodDetails.fish.pellets.foodTypeName').value
      advice = t('feeding.foodDetails.fish.pellets.advice').value
      {
        const tips = t('feeding.foodDetails.fish.pellets.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'frozen':
      baseDosagePerFish = 0.5
      foodTypeName = t('feeding.foodDetails.fish.frozen.foodTypeName').value
      advice = t('feeding.foodDetails.fish.frozen.advice').value
      {
        const tips = t('feeding.foodDetails.fish.frozen.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'nori':
      baseDosagePerFish = 1.0
      foodTypeName = t('feeding.foodDetails.fish.nori.foodTypeName').value
      advice = t('feeding.foodDetails.fish.nori.advice').value
      {
        const tips = t('feeding.foodDetails.fish.nori.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
    case 'custom':
      baseDosagePerFish = parseFloat(fishData.custom_fish_dosage) / fishCount
      foodTypeName = t('feeding.foodDetails.fish.custom.foodTypeName').value
      advice = t('feeding.foodDetails.fish.custom.advice').value
      {
        const tips = t('feeding.foodDetails.fish.custom.feedingTips').value
        feedingTips = Array.isArray(tips) ? tips : []
      }
      break
  }

  // 根据鱼尺寸和活跃度调整剂量
  baseDosagePerFish = adjustFishDosage(baseDosagePerFish, fishData.fish_size, fishData.fish_activity)

  // 计算每日总量
  const dailyTotal = baseDosagePerFish * fishCount
  
  // 计算单次剂量
  let singleDosage = dailyTotal
  let frequency = ''
  switch (fishData.feeding_frequency) {
    case 'twice':
      singleDosage = dailyTotal / 2
      frequency = t('feeding.options.feedingFrequency.twice').value
      break
    case 'three':
      singleDosage = dailyTotal / 3
      frequency = t('feeding.options.feedingFrequency.three').value
      break
    case 'multiple':
      singleDosage = dailyTotal / 5
      frequency = t('feeding.options.feedingFrequency.multiple').value
      break
    case 'once':
      frequency = t('feeding.options.feedingFrequency.once').value
      break
  }

  fishResults.singleDosage = Math.round(singleDosage * 100) / 100
  fishResults.dailyTotal = Math.round(dailyTotal * 100) / 100
  fishResults.foodType = foodTypeName
  fishResults.frequency = frequency
  fishResults.advice = advice
  fishResults.feedingTips = feedingTips
  fishResults.show = true
}

// 调整鱼粮剂量
const adjustFishDosage = (baseDosage: number, fishSize: string, activity: string): number => {
  let adjusted = baseDosage

  // 根据鱼尺寸调整
  switch (fishSize) {
    case 'small':
      adjusted *= 0.5
      break
    case 'large':
      adjusted *= 1.5
      break
    case 'xlarge':
      adjusted *= 2.0
      break
  }

  // 根据活跃度调整
  switch (activity) {
    case 'low':
      adjusted *= 0.7
      break
    case 'high':
      adjusted *= 1.3
      break
  }

  return adjusted
}

// 组件挂载
onMounted(() => {
  calculatorStore.getWaterVolume()
})
</script>

<style scoped>
.schedule-section {
  margin-top: 20px;
}

.schedule-card {
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.schedule-category {
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f1f1f1;
}

.schedule-category:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.schedule-category h5 {
  color: #007bff;
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.schedule-list {
  display: grid;
  gap: 15px;
}

.schedule-item {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.schedule-item h6 {
  margin: 0 0 10px 0;
  color: #007bff;
  font-size: 1rem;
}

.schedule-item ul {
  margin: 0;
  padding-left: 20px;
  color: #495057;
}

.schedule-item li {
  margin-bottom: 6px;
}

.tips-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #e7f3ff;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.tip-icon {
  font-size: 1.2rem;
}

.tip-text {
  color: #495057;
  font-size: 0.9rem;
}

.feeding-advice {
  margin-top: 15px;
  padding: 12px;
  background: #e7f3ff;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.feeding-advice h5 {
  margin: 0 0 8px 0;
  color: #007bff;
  font-size: 1rem;
}

.feeding-advice p {
  margin: 0 0 10px 0;
  color: #495057;
}

.feeding-advice ul {
  margin: 0;
  padding-left: 20px;
  color: #495057;
}

.feeding-advice li {
  margin-bottom: 4px;
}
</style>